/* Order-printed-stickers dialog launched from the Doorbell tile. Address
   form → /stickers/order/init → redirect to Flutterwave checkout for
   regular users, OR an instant "Order placed" success state for system
   admins (their orders are complimentary — same fulfilment email flow,
   no payment redirect). */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Loader2, Truck, Package, CheckCircle2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderStickersDialog({ open, onClose }) {
  const navigate = useNavigate();
  const [config, setConfig] = useState({ price_ngn: 1500, pack_size: 2, complimentary: false });
  const [form, setForm] = useState({
    recipient_name: "", phone: "", address_line: "", city: "", state: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [placed, setPlaced] = useState(null); // {order_id} once a comp order is created

  useEffect(() => {
    if (!open) return;
    setPlaced(null);
    axios.get(`${API}/stickers/config`, { withCredentials: true })
      .then((r) => setConfig(r.data))
      .catch(() => { /* defaults */ });
  }, [open]);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    // Minimal client validation — backend re-validates anyway.
    if (form.recipient_name.trim().length < 2) return toast.error("Please enter your name");
    if (form.phone.trim().length < 6) return toast.error("Please enter a phone number");
    if (form.address_line.trim().length < 4) return toast.error("Please enter your address");
    if (form.city.trim().length < 2 || form.state.trim().length < 2) return toast.error("City and state required");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/stickers/order/init`, form, { withCredentials: true });
      if (r.data?.free) {
        // System admin / complimentary path — no payment redirect, surface
        // the success state inline so they get confirmation + a quick link
        // to the admin orders board.
        setPlaced({ order_id: r.data.order_id });
        toast.success("Complimentary order placed", { description: `Order ${r.data.order_id} — fulfilment notified.` });
      } else {
        // Regular paid path — redirect to Flutterwave.
        window.location.href = r.data.checkout_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payment");
    } finally {
      setBusy(false);
    }
  };

  const isComp = config.complimentary;
  const ctaLabel = isComp
    ? "Place complimentary order"
    : `Pay ₦${(config.price_ngn || 1500).toLocaleString()} & ship`;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-md rounded-sm p-0 overflow-hidden bg-white" data-testid="order-stickers-dialog">
        <div className="bg-black text-white p-5">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              <Package className="w-4 h-4 text-emerald-400" />
              <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-400 font-body">Guard Stickers</span>
              {isComp && (
                <span className="text-[9px] uppercase tracking-widest font-bold text-amber-300 font-body border border-amber-400/40 rounded-sm px-1.5 py-0.5" data-testid="order-comp-pill">
                  Admin · Free
                </span>
              )}
            </div>
            <DialogTitle className="font-heading text-2xl font-black tracking-tighter">
              {isComp ? `${config.pack_size}-pack — complimentary` : `${config.pack_size}-pack — ₦${(config.price_ngn || 1500).toLocaleString()}`}
            </DialogTitle>
            <DialogDescription className="text-gray-300 font-body text-xs">
              Glossy weatherproof vinyl. Pre-printed with your doorbell QR. Ships in 2–3 business days.
            </DialogDescription>
          </DialogHeader>
        </div>

        {placed ? (
          <div className="p-6 text-center" data-testid="order-comp-success">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
            <p className="font-heading text-xl font-black tracking-tighter mb-1">Order placed</p>
            <p className="text-xs text-gray-600 font-body mb-4">
              Order <code className="bg-gray-100 px-1.5 py-0.5 rounded-sm text-[11px]">{placed.order_id}</code> — fulfilment ops notified.
            </p>
            <div className="flex gap-2">
              <Button onClick={() => { onClose?.(); navigate("/admin/stickers"); }} className="flex-1 bg-black hover:bg-gray-800 text-white rounded-sm font-body" data-testid="order-comp-open-admin">
                Open admin orders
              </Button>
              <Button onClick={onClose} variant="outline" className="rounded-sm font-body">Close</Button>
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-600 font-body">Name</Label>
                <Input value={form.recipient_name} onChange={update("recipient_name")} className="rounded-sm text-sm" placeholder="Full name" data-testid="order-recipient-name" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-600 font-body">Phone</Label>
                <Input value={form.phone} onChange={update("phone")} className="rounded-sm text-sm" placeholder="080…" data-testid="order-phone" />
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-600 font-body">Street address</Label>
              <Input value={form.address_line} onChange={update("address_line")} className="rounded-sm text-sm" placeholder="House no. + street" data-testid="order-address" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-600 font-body">City</Label>
                <Input value={form.city} onChange={update("city")} className="rounded-sm text-sm" placeholder="Lagos" data-testid="order-city" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-600 font-body">State</Label>
                <Input value={form.state} onChange={update("state")} className="rounded-sm text-sm" placeholder="Lagos" data-testid="order-state" />
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-600 font-body">Notes (optional)</Label>
              <Textarea value={form.notes} onChange={update("notes")} className="rounded-sm text-sm" placeholder="Gate colour, landmark, etc." rows={2} maxLength={200} data-testid="order-notes" />
            </div>
            <Button onClick={submit} disabled={busy} className="w-full bg-black hover:bg-gray-800 text-white rounded-sm font-body font-bold" data-testid="order-pay-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Truck className="w-4 h-4 mr-1.5" />}
              {busy ? (isComp ? "Placing…" : "Redirecting…") : ctaLabel}
            </Button>
            <p className="text-[10px] text-gray-500 font-body text-center">
              {isComp
                ? "System-admin complimentary order — no payment required."
                : "Payment by Flutterwave. You'll get a confirmation email immediately."}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
